import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { MeetingListItem } from '@ai-meeting/shared'
import DateInput from '@/components/DateInput'
import { EyeIcon, SearchIcon, TrashIcon } from '@/components/Icons'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import DeleteMeetingModal from '@/pages/History/components/DeleteMeetingModal'
import RemoveMeetingModal from '@/pages/History/components/RemoveMeetingModal'
import ShareMeetingFlow from '@/pages/History/components/ShareMeetingFlow'
import {
  deleteMeeting,
  deleteMeetingPermanently,
  fetchDeletedMeetingList,
  fetchMeetingList,
  hideMeetingFromHistory,
  restoreMeeting,
} from '@/services/meetings'
import { useStore } from '@/store/useStore'
import { formatDateTime } from '@/utils/formatDateTime'
import ProjectsPagination from './ProjectsPagination'

interface ProjectMeetingsTabProps {
  projectKey: string
  /** Parent refreshes project.meetingCount / lastMeetingAt after list mutations. */
  onMeetingsChanged?: () => void
}

type MeetingsSubTab = 'active' | 'deleted'

const PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 300
const TRASH_RETENTION_DAYS = 30

function formatDuration(minutes: number, minLabel: string): string {
  if (minutes <= 0) return '—'
  return `${minutes} ${minLabel}`
}

function formatDeletedRemainingDays(
  deletedAt: number | null | undefined,
  t: ReturnType<typeof useUiStrings>['t']
): string {
  if (!deletedAt) return fmt(t.remainingDays, { count: 0 })

  const elapsedDays = Math.floor((Date.now() - deletedAt) / 86_400_000)
  const remainingDays = Math.max(0, TRASH_RETENTION_DAYS - elapsedDays)
  return fmt(t.remainingDays, { count: remainingDays })
}

function ShareNodesIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path strokeLinecap="round" d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
    </svg>
  )
}

function actionChipClass(variant: 'view' | 'share' | 'delete'): string {
  const base =
    'inline-flex items-center gap-1 rounded-md px-[11px] py-[5px] text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50'
  if (variant === 'view') {
    return `${base} border border-indigo-100 bg-[var(--accent-light)]/70 text-[var(--accent)] dark:border-indigo-900`
  }
  if (variant === 'delete') {
    return `${base} border border-rose-200 bg-[var(--bg-secondary)] text-rose-500 dark:border-rose-900/50`
  }
  return `${base} border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-secondary)]`
}

/**
 * Meetings recorded against this project — Figma toolbar + table, with the
 * same list rules History uses (owned + shared-in), narrowed by
 * `backlogProjectKey`.
 */
export default function ProjectMeetingsTab({
  projectKey,
  onMeetingsChanged,
}: ProjectMeetingsTabProps) {
  const { t } = useUiStrings()
  const uiLocale = useStore((s) => s.uiLocale)
  const returnPath = `/projects/${projectKey}`

  const [subTab, setSubTab] = useState<MeetingsSubTab>('active')
  const [items, setItems] = useState<MeetingListItem[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [activeTotal, setActiveTotal] = useState<number | null>(null)
  const [deletedTotal, setDeletedTotal] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [reloadToken, setReloadToken] = useState(0)

  const [meetingToDelete, setMeetingToDelete] = useState<MeetingListItem | null>(null)
  const [meetingToShare, setMeetingToShare] = useState<MeetingListItem | null>(null)
  const [meetingToRemove, setMeetingToRemove] = useState<MeetingListItem | null>(null)
  const [meetingToPermanentlyDelete, setMeetingToPermanentlyDelete] =
    useState<MeetingListItem | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [trashActionLoading, setTrashActionLoading] = useState(false)

  function refetchFromPageOne(): void {
    setPage(1)
    setReloadToken((token) => token + 1)
    onMeetingsChanged?.()
  }

  function switchSubTab(next: MeetingsSubTab): void {
    if (next === subTab) return
    setLoading(true)
    setItems([])
    setSubTab(next)
  }

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [query])

  useEffect(() => {
    setPage(1)
  }, [debouncedQuery, dateFilter, subTab, projectKey])

  useEffect(() => {
    let active = true
    const countBase = { backlogProjectKey: projectKey, page: 1, limit: 1 }

    void Promise.all([
      fetchMeetingList({ ...countBase, status: 'recent' }),
      fetchDeletedMeetingList(countBase),
    ])
      .then(([activeRes, deletedRes]) => {
        if (!active) return
        setActiveTotal(activeRes.total)
        setDeletedTotal(deletedRes.total)
      })
      .catch(() => {
        if (!active) return
        setActiveTotal(null)
        setDeletedTotal(null)
      })

    return () => {
      active = false
    }
  }, [projectKey, reloadToken])

  useEffect(() => {
    let active = true

    let dateFrom: number | undefined
    let dateTo: number | undefined
    if (dateFilter) {
      const start = new Date(`${dateFilter}T00:00:00`)
      const end = new Date(`${dateFilter}T23:59:59.999`)
      dateFrom = start.getTime()
      dateTo = end.getTime()
    }

    const listQuery = {
      backlogProjectKey: projectKey,
      search: debouncedQuery.trim() || undefined,
      sortBy: 'createdAt' as const,
      sortOrder: 'desc' as const,
      dateFrom,
      dateTo,
      page,
      limit: PAGE_SIZE,
    }

    setLoading(true)
    setItems([])

    const fetcher =
      subTab === 'deleted'
        ? fetchDeletedMeetingList(listQuery)
        : fetchMeetingList({ ...listQuery, status: 'recent' })

    void fetcher
      .then((response) => {
        if (!active) return
        const pages = Math.max(1, response.totalPages)
        setItems(response.items)
        setTotal(response.total)
        setTotalPages(pages)
        // Keep page state in range so a shrunk result set cannot leave the
        // table stranded on an empty high page (display clamp alone is not enough).
        if (page > pages) setPage(pages)
        setError(null)
      })
      .catch((err: unknown) => {
        if (!active) return
        setError(err instanceof Error ? err.message : t.failedLoadMeetingList)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [
    projectKey,
    debouncedQuery,
    dateFilter,
    page,
    subTab,
    reloadToken,
    t.failedLoadMeetingList,
  ])

  async function handleConfirmDelete() {
    if (!meetingToDelete) return
    try {
      setDeleting(true)
      await deleteMeeting(meetingToDelete.id)
      setMeetingToDelete(null)
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedDeleteMeeting)
    } finally {
      setDeleting(false)
    }
  }

  async function handleRemoveFromHistory() {
    if (!meetingToRemove) return
    try {
      setRemoving(true)
      await hideMeetingFromHistory(meetingToRemove.id)
      setMeetingToRemove(null)
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedHideMeeting)
    } finally {
      setRemoving(false)
    }
  }

  async function handleRestoreMeeting(meeting: MeetingListItem) {
    try {
      setTrashActionLoading(true)
      await restoreMeeting(meeting.id)
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedRestoreMeeting)
    } finally {
      setTrashActionLoading(false)
    }
  }

  async function handlePermanentDeleteMeeting(meeting: MeetingListItem) {
    try {
      setTrashActionLoading(true)
      await deleteMeetingPermanently(meeting.id)
      setMeetingToPermanentlyDelete(null)
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedPermanentDeleteMeeting)
    } finally {
      setTrashActionLoading(false)
    }
  }

  const currentPage = Math.min(page, totalPages)
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, total)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="text-base font-bold text-[var(--text-primary)]">
            {t.projectDetailMeetingsHeading}
          </h2>
          <div
            role="tablist"
            aria-label={t.projectDetailMeetingsHeading}
            className="inline-flex items-center rounded-lg border border-[var(--border-color)]/60 bg-[var(--bg-tertiary)]/80 p-[3px]"
          >
            <button
              type="button"
              role="tab"
              aria-selected={subTab === 'active'}
              onClick={() => switchSubTab('active')}
              className={[
                'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors',
                subTab === 'active'
                  ? 'bg-[var(--bg-secondary)] font-semibold text-[var(--text-primary)] shadow-[var(--shadow-sm)]'
                  : 'font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
              ].join(' ')}
            >
              {t.activeTab}
              {activeTotal !== null && (
                <span
                  className={[
                    'rounded px-1.5 text-[11px] font-bold tabular-nums',
                    subTab === 'active'
                      ? 'bg-[var(--accent-light)] text-[var(--accent)]'
                      : 'text-[var(--text-tertiary)]',
                  ].join(' ')}
                >
                  {activeTotal}
                </span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={subTab === 'deleted'}
              onClick={() => switchSubTab('deleted')}
              className={[
                'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors',
                subTab === 'deleted'
                  ? 'bg-[var(--bg-secondary)] font-semibold text-[var(--text-primary)] shadow-[var(--shadow-sm)]'
                  : 'font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
              ].join(' ')}
            >
              {t.deletedTab}
              {deletedTotal !== null && (
                <span
                  className={[
                    'rounded px-1.5 text-[11px] font-medium tabular-nums',
                    subTab === 'deleted'
                      ? 'bg-[var(--accent-light)] font-bold text-[var(--accent)]'
                      : 'text-[var(--text-tertiary)]',
                  ].join(' ')}
                >
                  {deletedTotal}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-3">
          <label className="relative block min-w-0 flex-1 basis-[12rem] sm:max-w-xs">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.projectDetailMeetingsSearchPlaceholder}
              className="input-field w-full py-1.5 pl-9 text-xs"
            />
          </label>
          <DateInput
            value={dateFilter}
            onChange={setDateFilter}
            ariaLabel={t.filterByDate}
            placeholder={t.datePlaceholder}
            className="input-field w-[9rem] shrink-0 py-1.5 text-xs"
          />
        </div>
      </div>

      <div
        className="overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-[var(--shadow-sm)]"
        aria-busy={loading}
      >
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-[var(--border-color)] bg-[var(--bg-primary)]">
              <tr className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--text-tertiary)]">
                <th className="px-4 py-2.5 sm:pl-5">{t.meetingNameCol}</th>
                <th className="px-4 py-2.5">
                  {subTab === 'deleted' ? t.deletedDateCol : t.dateTimeCol}
                </th>
                <th className="px-4 py-2.5">
                  {subTab === 'deleted' ? t.remainingCol : t.durationCol}
                </th>
                <th className="px-4 py-2.5 text-center">{t.actionCol}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-sm text-[var(--text-secondary)] sm:px-5">
                    {t.loading}
                  </td>
                </tr>
              )}

              {!loading && error && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-sm text-[var(--error)] sm:px-5" role="alert">
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && items.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-10 text-center text-sm text-[var(--text-secondary)] sm:px-5"
                  >
                    {subTab === 'deleted' ? t.emptyTrash : t.projectDetailMeetingsEmpty}
                  </td>
                </tr>
              )}

              {!loading &&
                !error &&
                items.map((meeting) => {
                  const accessState =
                    meeting.accessState ??
                    (meeting.ownership === 'shared' ? 'shared' : 'owner')
                  const isOwner = accessState === 'owner'
                  const isAccessRemoved = accessState === 'access_removed'
                  const isShared = accessState === 'shared'
                  const shareableOk = meeting.shareable?.ok ?? meeting.hasMinutes
                  const shareDisabledReason =
                    meeting.shareable?.reason === 'processing'
                      ? t.shareDisabledProcessing
                      : meeting.shareable?.reason === 'error'
                        ? t.shareDisabledError
                        : t.shareDisabledNoContent

                  return (
                    <tr
                      key={meeting.id}
                      className={[
                        'border-t border-[var(--border-color)]',
                        isAccessRemoved ? 'opacity-50' : '',
                      ].join(' ')}
                    >
                      <td className="px-4 py-4 font-semibold text-[var(--text-primary)] sm:pl-5">
                        <span className="line-clamp-2 break-words">{meeting.title}</span>
                        {meeting.completed === false && subTab === 'active' && (
                          <span className="ml-2 inline-flex shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                            {t.meetingUnfinishedBadge}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 font-medium tabular-nums text-[var(--text-secondary)]">
                        {formatDateTime(
                          subTab === 'deleted' && meeting.deletedAt
                            ? meeting.deletedAt
                            : meeting.createdAt,
                          uiLocale
                        )}
                      </td>
                      <td className="px-4 py-4 font-medium text-[var(--text-secondary)]">
                        {subTab === 'deleted' ? (
                          <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-[var(--accent)] dark:bg-[var(--accent-light)]">
                            {formatDeletedRemainingDays(meeting.deletedAt, t)}
                          </span>
                        ) : (
                          formatDuration(meeting.durationMinutes, t.min)
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {isAccessRemoved ? (
                          <span className="flex justify-center text-xs font-semibold text-[var(--text-tertiary)]">
                            {t.accessRemoved}
                          </span>
                        ) : subTab === 'deleted' ? (
                          <div className="flex flex-wrap items-center justify-center gap-2">
                            <button
                              type="button"
                              disabled={trashActionLoading}
                              className={actionChipClass('view')}
                              onClick={() => void handleRestoreMeeting(meeting)}
                            >
                              {t.restore}
                            </button>
                            <button
                              type="button"
                              disabled={trashActionLoading}
                              className={actionChipClass('delete')}
                              onClick={() => setMeetingToPermanentlyDelete(meeting)}
                            >
                              {t.permanentDelete}
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center justify-center gap-1.5">
                            <Link
                              to={`/meetings/${meeting.id}`}
                              state={{ from: returnPath }}
                              className={actionChipClass('view')}
                            >
                              <EyeIcon className="h-3 w-3 shrink-0" />
                              {t.view}
                            </Link>
                            <button
                              type="button"
                              className={actionChipClass('share')}
                              disabled={!shareableOk}
                              title={!shareableOk ? shareDisabledReason : undefined}
                              onClick={() => setMeetingToShare(meeting)}
                            >
                              <ShareNodesIcon className="h-3 w-3 shrink-0" />
                              {t.share}
                            </button>
                            {isOwner ? (
                              <button
                                type="button"
                                className={actionChipClass('delete')}
                                onClick={() => setMeetingToDelete(meeting)}
                              >
                                <TrashIcon className="h-3 w-3 shrink-0" />
                                {t.delete}
                              </button>
                            ) : isShared ? (
                              <button
                                type="button"
                                className={actionChipClass('delete')}
                                onClick={() => setMeetingToRemove(meeting)}
                              >
                                <TrashIcon className="h-3 w-3 shrink-0" />
                                {t.removeFromHistory}
                              </button>
                            ) : null}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-[var(--border-color)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-xs text-[var(--text-secondary)] tabular-nums">
              {fmt(t.projectDetailMeetingsRange, {
                start: rangeStart,
                end: rangeEnd,
                total,
              })}
            </p>
            <ProjectsPagination page={currentPage} pageCount={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {meetingToDelete && (
        <DeleteMeetingModal
          title={t.deleteMeetingTitle}
          body={fmt(t.deleteMeetingBody, { title: meetingToDelete.title })}
          deleting={deleting}
          onClose={() => {
            if (!deleting) setMeetingToDelete(null)
          }}
          onConfirm={() => void handleConfirmDelete()}
        />
      )}

      {meetingToShare ? (
        <ShareMeetingFlow
          meetingId={meetingToShare.id}
          meetingTitle={meetingToShare.title}
          onClose={() => setMeetingToShare(null)}
          onShared={() => refetchFromPageOne()}
          onRevoked={() => refetchFromPageOne()}
        />
      ) : null}

      {meetingToRemove && (
        <RemoveMeetingModal
          removing={removing}
          onClose={() => {
            if (!removing) setMeetingToRemove(null)
          }}
          onConfirm={() => void handleRemoveFromHistory()}
        />
      )}

      {meetingToPermanentlyDelete && (
        <DeleteMeetingModal
          title={t.permanentDeleteMeetingTitle}
          body={fmt(t.permanentDeleteMeetingBody, { title: meetingToPermanentlyDelete.title })}
          confirmLabel={t.permanentDelete}
          deleting={trashActionLoading}
          onClose={() => {
            if (!trashActionLoading) setMeetingToPermanentlyDelete(null)
          }}
          onConfirm={() => void handlePermanentDeleteMeeting(meetingToPermanentlyDelete)}
        />
      )}
    </div>
  )
}
